import React, { useEffect, useMemo, useState } from 'react';
import { CarOutlined, EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form as FormAnt,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
} from 'antd';
import { useParams } from 'react-router-dom';
import { companies, IftaWithJumpBody } from '../../../../../../../../API/TT_ELD/Users/company';
import { users } from '../../../../../../../../API/TT_ELD/Users/user';
import { useVehicleSearch } from '../../../../../../../../Hooks/TT_ELD/Logs';
import { useIFTAReportDate } from '../../../../../../../../Hooks/TT_ELD/Vehicles';
import { US_STATES } from '../../../../../../../../Utils/data';

interface Params {
  [key: string]: string | undefined;
  id: string | undefined;
}

interface IftaDateResponseItem {
  month?: number[];
  quarter?: number[];
}

type IftaDatesResponse = Record<string, IftaDateResponseItem>;

interface VehicleOptionRow {
  id?: number | string;
  uid?: string;
  truck_number?: string;
}

interface IftaWithJumpUnitRow {
  id?: string;
  miles?: number | string;
  jump?: number | string;
  jump_list?: IftaJumpListRow[] | null;
  month?: number | string;
  state?: string;
  vehicle?: {
    truck_number?: string;
    vin?: string;
    year?: number | string;
  };
}

interface IftaJumpListRow {
  start_date?: string;
  end_date?: string;
  start_driverId?: string | number;
  end_driverId?: string | number;
  start_odometer?: string | number;
  end_odometer?: string | number;
  jump?: string | number;
  start_coordinates?: {
    lat?: number | string;
    lng?: number | string;
  };
  end_coordinates?: {
    lat?: number | string;
    lng?: number | string;
  };
}

interface IftaWithJumpStatesRow {
  state?: string;
  total?: number | string;
}

interface IftaWithJumpAllResponse {
  data?: IftaWithJumpUnitRow[];
  count?: number;
  totalMiles?: number | string;
  totalJumps?: number | string;
  iftaMiles?: number | string;
  nonIftaMiles?: number | string;
}

interface IftaWithJumpFormValues {
  year?: number;
  quarter?: number | 'wholeYear' | 'byMonth';
  month?: number;
  states?: string[];
  vehicles?: Array<number | string>;
}

interface UnitTableRow {
  key: string;
  vehicle: string;
  vin: string;
  state: string;
  miles: number;
  jump: number;
  jumpList: IftaJumpListRow[] | null;
  month: string;
}

interface StatesTableRow {
  key: string;
  state: string;
  total: number;
}

const monthText = (month: number | string | undefined) => {
  if (month === undefined || month === null || month === '') return '-';

  const monthIndex = Number(month);
  if (!Number.isFinite(monthIndex) || monthIndex < 1 || monthIndex > 12) return String(month);

  return new Date(2000, monthIndex - 1, 1).toLocaleString('en-US', { month: 'long' });
};

const toNumber = (value: number | string | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStatesRows = (response: any): IftaWithJumpStatesRow[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getAllResponse = (response: any): IftaWithJumpAllResponse => {
  if (response && typeof response === 'object') return response as IftaWithJumpAllResponse;
  return {};
};

const normalizeIftaDatesPayload = (payload: any): IftaDatesResponse => {
  if (!payload || typeof payload !== 'object') return {};

  // API can come either as {2026: {...}} or wrapped as {data: {2026: {...}}}
  const raw = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
  const result: IftaDatesResponse = {};

  Object.entries(raw).forEach(([yearKey, value]) => {
    const yearNumber = Number(yearKey);
    if (!Number.isFinite(yearNumber)) return;

    const row = value as IftaDateResponseItem;
    result[String(yearNumber)] = {
      month: Array.isArray(row?.month) ? row.month : [],
      quarter: Array.isArray(row?.quarter) ? row.quarter : [],
    };
  });

  return result;
};

const IftaWithJump = () => {
  const [form] = FormAnt.useForm<IftaWithJumpFormValues>();
  const { id } = useParams<Params>();
  const companyId = id ? Number(id) : undefined;

  const selectedYear = FormAnt.useWatch('year', form);
  const selectedReportType = FormAnt.useWatch('quarter', form);
  const [activeTableTab, setActiveTableTab] = useState<'units' | 'states'>('units');
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [unitRows, setUnitRows] = useState<IftaWithJumpUnitRow[]>([]);
  const [statesRows, setStatesRows] = useState<IftaWithJumpStatesRow[]>([]);
  const [totals, setTotals] = useState({
    iftaMiles: 0,
    nonIftaMiles: 0,
    totalMiles: 0,
    totalJumps: 0,
  });
  const [jumpModalOpen, setJumpModalOpen] = useState(false);
  const [selectedJumpList, setSelectedJumpList] = useState<IftaJumpListRow[]>([]);
  const [jumpDriverNameMap, setJumpDriverNameMap] = useState<Record<string, string>>({});
  const [loadingJumpDrivers, setLoadingJumpDrivers] = useState(false);

  const { data: iftaDatesData } = useIFTAReportDate(companyId);
  const vehicleData = useVehicleSearch('', companyId) as {
    data?: { data?: VehicleOptionRow[] };
  };

  const iftaDates: IftaDatesResponse = useMemo(() => {
    return normalizeIftaDatesPayload(iftaDatesData);
  }, [iftaDatesData]);

  const yearOptions = useMemo(() => {
    return Object.keys(iftaDates)
      .map((year) => Number(year))
      .filter((year) => Number.isFinite(year))
      .sort((a, b) => b - a)
      .filter((year) => {
        const row = iftaDates[String(year)];
        return Boolean((row?.quarter?.length || 0) > 0 || (row?.month?.length || 0) > 0);
      })
      .map((year) => ({
        label: String(year),
        value: year,
      }));
  }, [iftaDates]);

  const quarterOptions = useMemo(() => {
    if (!selectedYear) return [];
    const currentYearData = iftaDates[String(selectedYear)];
    const quarters = currentYearData?.quarter ?? [];

    return [
      ...quarters.map((quarter) => ({
        label: `Quarter ${quarter}`,
        value: quarter as number | 'wholeYear' | 'byMonth',
      })),
      { label: 'Whole year', value: 'wholeYear' as const },
      { label: 'By month', value: 'byMonth' as const },
    ];
  }, [iftaDates, selectedYear]);

  const monthOptions = useMemo(() => {
    if (!selectedYear) return [];
    const currentYearData = iftaDates[String(selectedYear)];
    const months = currentYearData?.month ?? [];

    return months.map((month) => ({
      label: monthText(month),
      value: month,
    }));
  }, [iftaDates, selectedYear]);

  const getQuarterValuesByYear = (year?: number) => {
    if (!year) return [];
    return (iftaDates[String(year)]?.quarter ?? []).map((quarter) => Number(quarter));
  };

  const vehicleOptions = useMemo(() => {
    const rows = vehicleData?.data?.data ?? [];
    return rows
      .map((vehicle) => ({
        label: vehicle.truck_number || '-',
        value: Number(vehicle.id),
      }))
      .filter((option) => Number.isFinite(option.value));
  }, [vehicleData?.data?.data]);

  const statesOptions = useMemo(
    () =>
      US_STATES.map((state) => ({
        label: state.text,
        value: state.value,
      })),
    [],
  );

  const tableUnitRows: UnitTableRow[] = useMemo(
    () =>
      unitRows.map((row, index) => ({
        key: row.id || `${row.vehicle?.truck_number || 'unit'}-${index}`,
        vehicle: row.vehicle?.truck_number || '-',
        vin: row.vehicle?.vin || '-',
        state: row.state || '-',
        miles: toNumber(row.miles),
        jump: toNumber(row.jump),
        jumpList: Array.isArray(row.jump_list) ? row.jump_list : null,
        month: monthText(row.month),
      })),
    [unitRows],
  );

  const tableStatesRows: StatesTableRow[] = useMemo(
    () =>
      statesRows
        .filter((row) => row.state)
        .map((row, index) => ({
          key: `${row.state}-${index}`,
          state: row.state || '-',
          total: toNumber(row.total),
        })),
    [statesRows],
  );

  const submitReport = async (values: IftaWithJumpFormValues) => {
    if (!companyId || !values.year || !values.quarter) {
      message.error('Please select date range and report type');
      return;
    }

    const body: IftaWithJumpBody = {
      companyId,
      year: values.year,
      states: values.states ?? [],
      vehicles: (values.vehicles ?? [])
        .map((vehicleId) => Number(vehicleId))
        .filter((vehicleId) => Number.isFinite(vehicleId)),
      page: 1,
      perPage: 50,
    };

    if (typeof values.quarter === 'number') {
      body.quarter = values.quarter;
    }

    if (values.quarter === 'byMonth') {
      if (!values.month) {
        message.error('Please select month');
        return;
      }
      const selectedMonth = Number(values.month);
      if (Number.isFinite(selectedMonth)) {
        body.month = selectedMonth;
      }
    }

    try {
      setLoading(true);

      const [statesResponse, allResponse] = await Promise.all([
        companies.iftaWithJumpGetStatesAll(companyId, body),
        companies.iftaWithJumpAll(companyId, body),
      ]);

      const statesData = getStatesRows(statesResponse);
      const allData = getAllResponse(allResponse);
      const allRows = Array.isArray(allData?.data) ? allData.data : [];

      const totalMiles = toNumber(allData?.totalMiles);
      const totalJumps = toNumber(allData?.totalJumps);
      const iftaMiles =
        allData?.iftaMiles !== undefined ? toNumber(allData?.iftaMiles) : toNumber(allData?.totalMiles);
      const nonIftaMiles =
        allData?.nonIftaMiles !== undefined ? toNumber(allData?.nonIftaMiles) : 0;

      setStatesRows(statesData);
      setUnitRows(allRows);
      setTotals({
        iftaMiles,
        nonIftaMiles,
        totalMiles: totalMiles || iftaMiles + nonIftaMiles,
        totalJumps,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!yearOptions.length) return;
    if (form.getFieldValue('year')) return;

    const defaultYear = yearOptions[0].value;
    const defaultQuarters = (iftaDates[String(defaultYear)]?.quarter ?? []).map((quarter) =>
      Number(quarter),
    );

    form.setFieldsValue({
      year: defaultYear,
      quarter: defaultQuarters[0],
      month: undefined,
      states: [],
      vehicles: [],
    });
  }, [form, iftaDates, yearOptions]);

  useEffect(() => {
    if (initialLoaded) return;
    const values = form.getFieldsValue();
    if (!values.year || !values.quarter) return;

    setInitialLoaded(true);
    void submitReport(values);
  }, [form, initialLoaded, selectedYear, selectedReportType]);

  const resolveDriverNames = async (jumpList: IftaJumpListRow[]) => {
    const ids = Array.from(
      new Set(
        jumpList
          .flatMap((jump) => [jump.start_driverId, jump.end_driverId])
          .filter((id): id is string | number => id !== null && id !== undefined && id !== ''),
      ),
    );

    const missingIds = ids.filter((id) => !jumpDriverNameMap[String(id)]);
    if (!missingIds.length) return;

    setLoadingJumpDrivers(true);
    try {
      const responses = await Promise.all(
        missingIds.map(async (id) => {
          const userData = await users.userData(id);
          const firstName = userData?.first_name || '';
          const secondName = userData?.second_name || '';
          const name = `${firstName} ${secondName}`.trim() || String(id);
          return { id: String(id), name };
        }),
      );

      setJumpDriverNameMap((prev) => {
        const next = { ...prev };
        responses.forEach((item) => {
          next[item.id] = item.name;
        });
        return next;
      });
    } finally {
      setLoadingJumpDrivers(false);
    }
  };

  const openJumpModal = async (jumpList: IftaJumpListRow[] | null) => {
    const rows = Array.isArray(jumpList) ? jumpList : [];
    setSelectedJumpList(rows);
    setJumpModalOpen(true);
    if (rows.length) {
      await resolveDriverNames(rows);
    }
  };

  const getDriverName = (id?: string | number) => {
    if (id === undefined || id === null || id === '') return '-';
    return jumpDriverNameMap[String(id)] || String(id);
  };

  const unitColumns = [
    {
      title: 'Vehicle',
      dataIndex: 'vehicle',
      key: 'vehicle',
      width: 260,
    },
    {
      title: 'VIN',
      dataIndex: 'vin',
      key: 'vin',
      width: 260,
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 180,
    },
    {
      title: 'Miles',
      dataIndex: 'miles',
      key: 'miles',
      width: 180,
      render: (value: number) => value.toLocaleString('en-US'),
    },
    {
      title: 'Jump',
      dataIndex: 'jump',
      key: 'jump',
      width: 140,
      render: (value: number) => value.toLocaleString('en-US'),
    },
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 180,
    },
    {
      title: 'Jump details',
      dataIndex: 'jumpList',
      key: 'jumpList',
      width: 170,
      render: (jumpList: IftaJumpListRow[] | null) => {
        const hasJumps = Array.isArray(jumpList) && jumpList.length > 0;
        if (!hasJumps) return '-';
        return <Button onClick={() => void openJumpModal(jumpList)}>Show more</Button>;
      },
    },
  ];

  const statesColumns = [
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 220,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 220,
      render: (value: number) => value.toLocaleString('en-US'),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <FormAnt
          form={form}
          layout="vertical"
          onFinish={submitReport}
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={12} lg={6}>
              <FormAnt.Item
                label="Date range"
                name="year"
                rules={[{ required: true, message: 'Please select year' }]}
              >
                <Select
                  placeholder="Select year"
                  options={yearOptions}
                  onChange={(year) => {
                    const quarters = getQuarterValuesByYear(year);
                    form.setFieldsValue({
                      quarter: quarters[0],
                      month: undefined,
                    });
                  }}
                />
              </FormAnt.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
              <FormAnt.Item
                label="Report Type"
                name="quarter"
                rules={[{ required: true, message: 'Please select report type' }]}
              >
                <Select
                  placeholder="Select report type"
                  options={quarterOptions}
                  disabled={!selectedYear || quarterOptions.length === 0}
                  onChange={(value) => {
                    if (value !== 'byMonth') {
                      form.setFieldsValue({ month: undefined });
                    }
                  }}
                />
              </FormAnt.Item>
            </Col>

            {selectedReportType === 'byMonth' && (
              <Col xs={24} sm={24} md={12} lg={6}>
                <FormAnt.Item
                  label="Month"
                  name="month"
                  rules={[{ required: true, message: 'Please select month' }]}
                >
                  <Select
                    allowClear
                    placeholder="Select month"
                    options={monthOptions}
                    disabled={!selectedYear || monthOptions.length === 0}
                  />
                </FormAnt.Item>
              </Col>
            )}
          </Row>

          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={12} lg={6}>
              <FormAnt.Item label="States" name="states">
                <Select mode="multiple" allowClear placeholder="Please select" options={statesOptions} />
              </FormAnt.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
              <FormAnt.Item label="Vehicles" name="vehicles">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="Please select"
                  options={vehicleOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </FormAnt.Item>
            </Col>
          </Row>

          <FormAnt.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit
            </Button>
          </FormAnt.Item>
        </FormAnt>

        <Tabs
          activeKey={activeTableTab}
          onChange={(key) => setActiveTableTab(key as 'units' | 'states')}
          items={[
            {
              key: 'units',
              label: (
                <span>
                  <CarOutlined style={{ marginRight: 8 }} />
                  Units
                </span>
              ),
            },
            {
              key: 'states',
              label: (
                <span>
                  <EnvironmentOutlined style={{ marginRight: 8 }} />
                  States
                </span>
              ),
            },
          ]}
        />

        <Spin spinning={loading}>
          <div style={{ marginBottom: 20 }}>
            <Row gutter={[0, 8]}>
              <Col xs={24} sm={8} style={{ padding: '0 12px 0 0', borderRight: '1px solid #f0f0f0' }}>
                <Statistic title="IFTA miles" value={totals.iftaMiles} formatter={(v) => Number(v).toLocaleString('en-US')} />
              </Col>
              <Col xs={24} sm={8} style={{ padding: '0 12px', borderRight: '1px solid #f0f0f0' }}>
                <Statistic
                  title="Non-IFTA miles"
                  value={totals.nonIftaMiles}
                  formatter={(v) => Number(v).toLocaleString('en-US')}
                />
              </Col>
              <Col xs={24} sm={8} style={{ padding: '0 0 0 12px' }}>
                <Statistic
                  title="Total miles"
                  value={totals.totalMiles}
                  formatter={(v) => Number(v).toLocaleString('en-US')}
                />
              </Col>
            </Row>
            <Row gutter={[0, 8]} style={{ marginTop: 12 }}>
              <Col xs={24} sm={8} style={{ padding: '0 12px 0 0' }}>
                <Statistic
                  title="Total jumps"
                  value={totals.totalJumps}
                  formatter={(v) => Number(v).toLocaleString('en-US')}
                />
              </Col>
            </Row>
          </div>

          {activeTableTab === 'units' ? (
            <Table
              columns={unitColumns}
              dataSource={tableUnitRows}
              pagination={false}
              locale={{ emptyText: <Empty description="No data" /> }}
              scroll={{ x: 1100, y: 500 }}
            />
          ) : (
            <Table
              columns={statesColumns}
              dataSource={tableStatesRows}
              pagination={false}
              locale={{ emptyText: <Empty description="No data" /> }}
              scroll={{ x: 800, y: 500 }}
            />
          )}
        </Spin>
      </Space>

      <Modal
        width={1200}
        title="Jump details"
        open={jumpModalOpen}
        onCancel={() => setJumpModalOpen(false)}
        footer={null}
      >
        <Spin spinning={loadingJumpDrivers}>
          <Descriptions size="small" bordered column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Count">{selectedJumpList.length}</Descriptions.Item>
          </Descriptions>
          <Table
            rowKey={(_, index) => String(index)}
            pagination={false}
            dataSource={selectedJumpList}
            scroll={{ x: 1400, y: 450 }}
            columns={[
              {
                title: 'Start date',
                dataIndex: 'start_date',
                key: 'start_date',
                width: 180,
                render: (value: string) => (value ? moment(value).format('MM-DD-YYYY HH:mm:ss') : '-'),
              },
              {
                title: 'End date',
                dataIndex: 'end_date',
                key: 'end_date',
                width: 180,
                render: (value: string) => (value ? moment(value).format('MM-DD-YYYY HH:mm:ss') : '-'),
              },
              {
                title: 'Start driver',
                dataIndex: 'start_driverId',
                key: 'start_driverId',
                width: 220,
                render: (value: string | number) => getDriverName(value),
              },
              {
                title: 'End driver',
                dataIndex: 'end_driverId',
                key: 'end_driverId',
                width: 220,
                render: (value: string | number) => getDriverName(value),
              },
              {
                title: 'Start odometer',
                dataIndex: 'start_odometer',
                key: 'start_odometer',
                width: 140,
                render: (value: string | number) => (value ?? '-'),
              },
              {
                title: 'End odometer',
                dataIndex: 'end_odometer',
                key: 'end_odometer',
                width: 140,
                render: (value: string | number) => (value ?? '-'),
              },
              {
                title: 'Jump',
                dataIndex: 'jump',
                key: 'jump',
                width: 120,
                render: (value: string | number) => toNumber(value).toLocaleString('en-US'),
              },
              {
                title: 'Start coordinates',
                dataIndex: 'start_coordinates',
                key: 'start_coordinates',
                width: 220,
                render: (value: IftaJumpListRow['start_coordinates']) =>
                  value ? `${value.lat ?? '-'}, ${value.lng ?? '-'}` : '-',
              },
              {
                title: 'End coordinates',
                dataIndex: 'end_coordinates',
                key: 'end_coordinates',
                width: 220,
                render: (value: IftaJumpListRow['end_coordinates']) =>
                  value ? `${value.lat ?? '-'}, ${value.lng ?? '-'}` : '-',
              },
            ]}
          />
        </Spin>
      </Modal>
    </Card>
  );
};

export default IftaWithJump;
